import type { AnimationGeneratorType, Easing } from 'motion'
import { css } from 'styled-components'

const breakpoints = {
  'iphone6': '375px',
  'mobile': '414px',
  'phablet': '480px',
  'nexus7': '603px',
  'tablet': '768px',
  'desktop-xs': '992px',
  'container': '1170px',
  'desktop-md': '1200px',
  'desktop': '1440px',
  'desktop-wide': '1650px',
  'large-screen': '1900px'
} as const

type TypeBreakpointKey = keyof typeof breakpoints
type TypeMq = {
  greaterThan: (device:TypeBreakpointKey) => string
  lessThan: (device:TypeBreakpointKey) => string
  between: (fromDevice: TypeBreakpointKey, untilDevice: TypeBreakpointKey) => string
}

function pxToEm(breakpoint: TypeBreakpointKey, baseRemPx = 16): string {
  const breakpointValue = breakpoints[breakpoint]
  if (!breakpointValue) throw new Error(`Breakpoint "${breakpoint}" not found.`)

  const parsedValue = parseInt(breakpointValue, 10)
  const emValue = parsedValue / baseRemPx // Assuming 1rem = 16px

  return `${emValue}em`
}

export const mq:TypeMq = {
  greaterThan: (device) => `@media (min-width: ${pxToEm(device)})`,
  lessThan: (device) => `@media (max-width: ${pxToEm(device)})`,
  between: (fromDevice, untilDevice) => `@media (min-width: ${pxToEm(fromDevice)}) and (max-width: ${pxToEm(untilDevice) })`
}

export const vw = (size:number, breakpoint:TypeBreakpointKey | number, unit = 'vw') => {
  const screenWidth = breakpoints[breakpoint] ?? breakpoint
  return `${(100 * size / parseInt(screenWidth))}${unit}`
}

export const vh = (size, breakpoint:TypeBreakpointKey | number) => {
  const screenWidth = breakpoints[breakpoint] ?? breakpoint
  return `${(100 * parseInt(size) / parseInt(screenWidth))}vh`
}

export const getEasing = (duration = 300) => (
  {
    duration,
    ease: [.25, .1, .25, 1]
  }
)

export const getTransition = (duration = .3) => ({
  duration,
  type: 'tween' as AnimationGeneratorType,
  ease: [.25, .1, .25, 1] as Easing
}) as const

export type Col = 'auto' | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
interface iColumns {
  gutter: {
    outside: boolean,
    width: number
  },
  number: number,
  width: number
}

export interface iGrid {
  readonly mobile?: {
    maxWidth: number,
    columns: iColumns
  },
  readonly tablet?: {
    maxWidth: number,
    columns: iColumns
  },
  readonly desktop?: {
    maxWidth: number,
    columns: iColumns
  },
  readonly withoutGutter?: boolean,
}

export const grid:iGrid = {
  mobile: {
    maxWidth: 414, // mobile
    columns: {
      gutter: { outside: true, width: 17 },
      number: 6,
      width: 50
    }
  },
  tablet: {
    maxWidth: 768,
    columns: {
      gutter: { outside: true, width: 12 },
      number: 12,
      width: 49
    }
  },
  desktop: {
    maxWidth: 1440,
    columns: {
      gutter: { outside: true, width: 50 },
      number: 12,
      width: 70
    }
  },
  withoutGutter: false
}


export const designGrid = ({ mobile = grid.mobile, tablet = grid.tablet, desktop = grid.desktop, withoutGutter = grid.withoutGutter }:iGrid) => css`
  display: grid;
  grid-template-columns: ${`repeat(${mobile.columns.number}, ${vw(withoutGutter ? mobile.columns.width + mobile.columns.gutter.width : mobile.columns.width, mobile.maxWidth, '%')})`};
  grid-column-gap: ${withoutGutter ? 'unset' : `${vw(mobile.columns.gutter.width, mobile.maxWidth, '%')}`};
  grid-row-gap: 0;
  justify-content: center;
  width: 100%;

  @media (min-width: 768px) {
    grid-column-gap: ${withoutGutter ? 'unset' : `${vw(tablet.columns.gutter.width, tablet.maxWidth, '%')}`};
    grid-template-columns: ${`repeat(${tablet.columns.number}, ${vw(withoutGutter ? tablet.columns.width + tablet.columns.gutter.width : tablet.columns.width, tablet.maxWidth, '%')})`};
  }

  @media (min-width: 1440px) {
    grid-column-gap: ${withoutGutter ? 'unset' : `${vw(desktop.columns.gutter.width, desktop.maxWidth, '%')}`};
    grid-template-columns: ${`repeat(${desktop.columns.number}, ${vw(withoutGutter ? desktop.columns.width + desktop.columns.gutter.width : desktop.columns.width, desktop.maxWidth, '%')})`};
  }
`
